import axios from 'axios';
import { getAuthToken } from '@redpanda/utils';
import { Role } from '@redpanda/types';

const API_URL = process.env.REACT_APP_ACCOUNTING_BACKEND_URL || 'http://localhost:5002/api';
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// 添加請求攔截器自動設定 Authorization header
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const identityApi = axios.create({
  baseURL: IDENTITY_API_URL,
  withCredentials: true,
});

// 添加 identityApi 的請求攔截器
identityApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const subscriptionApi = axios.create({
  baseURL: SUBSCRIPTION_API_URL,
  withCredentials: true,
});

// 添加 subscriptionApi 的請求攔截器
subscriptionApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 網路錯誤檢測
const networkErrorHandler = (error: any) => {
  const isRealNetworkError = (
    (!error.response && (
      error.code === 'ERR_NETWORK' || 
      error.code === 'ECONNREFUSED' || 
      error.code === 'ERR_CONNECTION_REFUSED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ENETUNREACH' ||
      error.code === 'EAI_AGAIN'
    )) ||
    (error.response?.status >= 500 && error.response?.status <= 599)
  );
  
  if (isRealNetworkError) {
    console.error('Network Error detected:', error);
    // window.location.href = '/maintenance.html'; // 暫時停用跳轉
    return;
  }
  
  return Promise.reject(error);
};

// 為所有 API 實例添加網路錯誤檢測
[api, identityApi, subscriptionApi].forEach(apiInstance => {
  apiInstance.interceptors.response.use(
    response => response,
    networkErrorHandler
  );
});

// --- 會計科目 APIs ---
export const getAccounts = (page = 1, pageSize = 20, isActive = true) => api.get('/accounts', {
  params: { page, pageSize, isActive }
});
export const getAccountById = (id: string) => api.get(`/accounts/${id}`);
export const createAccount = (data: any) => api.post('/accounts', data);
export const updateAccount = (id: string, data: any) => api.put(`/accounts/${id}`, data);
export const deleteAccount = (id: string) => api.delete(`/accounts/${id}`);
export const bulkDeleteAccounts = (ids: string[]) => api.delete('/accounts/bulk', { data: { ids } });
export const checkAccountUsage = (id: string) => api.get(`/accounts/${id}/check-usage`);
export const initDefaultAccounts = (language = 'zh') => api.post('/accounts/init-defaults', { language });

// --- 記帳分錄 APIs ---
export const getJournalEntries = (page = 1, pageSize = 20) => api.get('/journals', {
  params: { page, pageSize }
});
export const getJournalEntryById = (id: string) => api.get(`/journals/${id}`);
export const createJournalEntry = (data: any) => api.post('/journals', data);
export const updateJournalEntry = (id: string, data: any) => api.put(`/journals/${id}`, data);
export const deleteJournalEntry = (id: string) => api.delete(`/journals/${id}`);
export const bulkDeleteJournalEntries = (ids: string[]) => api.delete('/journals/bulk', { data: { ids } });
export const postJournalEntry = (id: string) => api.patch(`/journals/${id}/post`);
export const unpostJournalEntry = (id: string) => api.patch(`/journals/${id}/unpost`);
export const bulkPostJournalEntries = (ids: string[]) => api.post('/journals/bulk-post', { ids });
export const bulkUnpostJournalEntries = (ids: string[]) => api.post('/journals/bulk-unpost', { ids });

// --- 發票 APIs ---
/** 發票列表：搜尋文字、狀態、年月由後端查全部發票（年份單獨＝整年） */
export const getInvoices = (page = 1, pageSize = 20, month?: string, year?: string, archived?: boolean, filters: { search?: string; status?: string } = {}) => api.get('/invoices', {
  params: { page, pageSize, month, year, archived, ...filters }
});
export const getInvoiceById = (id: string) => api.get(`/invoices/${id}`);
export const createInvoice = (data: any) => api.post('/invoices', data);
export const updateInvoice = (id: string, data: any) => api.patch(`/invoices/${id}`, data);
export const deleteInvoice = (id: string) => api.delete(`/invoices/${id}`);
export const bulkDeleteInvoices = (ids: string[]) => api.delete('/invoices/bulk', { data: { ids } });
export const bulkDeleteArchivedInvoices = (ids: string[]) => api.delete('/invoices/archived/bulk', { data: { ids } });
export const updateInvoiceStatus = (id: string, status: string) => api.patch(`/invoices/${id}/status`, { status });
export const restoreArchivedInvoices = (invoiceIds: string[]) => api.post('/invoices/restore-selected', { invoiceIds });

// --- 財務報表 APIs ---
export const getAccountSummary = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/account-summary${params}`);
};
export const getJournalSummary = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/journal-summary${params}`);
};
export const getInvoiceSummary = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/invoice-summary${params}`);
};
export const getAccountsReceivablePayable = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/accounts-receivable-payable${params}`);
};
export const getMonthlyIncomeExpense = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/monthly-income-expense${params}`);
};
export const getPendingTasks = (year?: string) => {
  const params = year ? `?year=${year}` : '';
  return api.get(`/reports/pending-tasks${params}`);
};
export const getTrialBalance = (year?: string) => {
  return api.get('/reports/trial-balance', { params: year ? { year } : {} });
};
export const getIncomeStatement = (year?: string) => {
  return api.get('/reports/income-statement', { params: year ? { year } : {} });
};
export const getBalanceSheet = (year?: string) => {
  return api.get('/reports/balance-sheet', { params: year ? { year } : {} });
};

// --- 儀表板 APIs ---
export const getDashboardStats = () => api.get('/dashboard/stats');

// --- 發票限制 APIs ---
export const getInvoiceLimitInfo = () => {
  return subscriptionApi.get(`/subscriptions/invoice-limit`);
};

// --- 發票封存 APIs ---
export const archiveSelectedInvoices = (invoiceIds: string[]) => api.post('/invoices/archive-selected', { invoiceIds });

// --- 身份驗證相關 APIs ---
export const getProfile = () => identityApi.get('/profile');

// --- 用戶管理 APIs (Admin) ---
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');
export const getPendingUsers = () => identityApi.get('/admin/users/pending');

// 用戶角色管理 (RESTful: PATCH /users/:id/role)
export const updateUserRole = (userId: string, role: Role) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });

// 核准／拒絕用戶：與 CRM 相同走 /approve、/reject（/approve 會設定 allowedSystems，預設 ALL）
export const approveUser = (userId: string, role: Role, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);

// 刪除用戶 (RESTful: DELETE /users/:id)
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);

// --- 會計年度 APIs ---
export const getFiscalYears = (page: number = 1, pageSize: number = 20) => 
  api.get('/fiscal-years', { params: { page, pageSize } });
export const getFiscalYearById = (id: string) => api.get(`/fiscal-years/${id}`);
export const getActiveFiscalYear = () => api.get('/fiscal-years/active');
export const createFiscalYear = (data: any) => api.post('/fiscal-years', data);
export const updateFiscalYear = (id: string, data: any) => api.put(`/fiscal-years/${id}`, data);
export const deleteFiscalYear = (id: string) => api.delete(`/fiscal-years/${id}`);
export const activateFiscalYear = (id: string) => api.patch(`/fiscal-years/${id}/activate`);
export const closeFiscalYear = (id: string) => api.patch(`/fiscal-years/${id}/close`);
export const generateFiscalYearPeriods = (id: string) => api.post(`/fiscal-years/${id}/generate-periods`);

// 期間管理
export const deleteAccountingPeriod = (periodId: string) => api.delete(`/fiscal-years/periods/${periodId}`);

// --- 期間結算 APIs ---
export const getPeriodClosings = (page: number = 1, pageSize: number = 20) =>
  api.get('/period-closings', { params: { page, pageSize } });
export const getPeriodClosingById = (id: string) => api.get(`/period-closings/${id}`);
export const startPeriodClosing = (data: any) => api.post('/period-closings', data);
export const updatePeriodClosingNotes = (id: string, notes: string) =>
  api.patch(`/period-closings/${id}/notes`, { notes });
export const reopenPeriod = (periodId: string) => api.patch(`/periods/${periodId}/reopen`);
export const getPeriodClosingAuditLogs = (id: string) => api.get(`/period-closings/${id}/audit-logs`);

// --- 期間比較 APIs ---
export const getPeriodComparison = (data: any) => api.post('/period-comparison', data);
export const getAvailablePeriodsForComparison = () => api.get('/period-comparison/available-periods');

// --- 清理功能 APIs ---
export const clearPeriodClosings = () => api.delete('/dev/period-closings');

// === 稅務報表 APIs ===

// 獲取 401 營業人銷售額與稅額申報書
export const getTaxReport401 = (period: string) =>
  api.get(`/reports/tax-401/${period}`);

// 獲取 403 營業人進項稅額申報書
export const getTaxReport403 = (period: string) =>
  api.get(`/reports/tax-403/${period}`);

// 獲取營業稅試算結果
export const getTaxSummary = (period: string) =>
  api.get(`/reports/tax-summary/${period}`);

// 獲取可用申報期間列表
export const getTaxPeriods = () =>
  api.get('/reports/tax-periods');

// === 銀行帳戶管理 APIs ===

export const bankAccountService = {
  // 獲取銀行帳戶列表
  getBankAccounts: (page: number = 1, pageSize: number = 20) =>
    api.get('/bank-accounts', { params: { page, pageSize } }),

  // 獲取銀行帳戶統計摘要
  getBankAccountSummary: () =>
    api.get('/bank-accounts/summary'),

  // 獲取單一銀行帳戶
  getBankAccountById: (id: string) =>
    api.get(`/bank-accounts/${id}`),

  // 建立銀行帳戶
  createBankAccount: (data: any) =>
    api.post('/bank-accounts', data),

  // 更新銀行帳戶
  updateBankAccount: (id: string, data: any) =>
    api.put(`/bank-accounts/${id}`, data),

  // 刪除銀行帳戶
  deleteBankAccount: (id: string) =>
    api.delete(`/bank-accounts/${id}`),

  // 獲取帳戶交易記錄
  getBankTransactions: (accountId: string, page: number = 1, pageSize: number = 20) =>
    api.get(`/bank-accounts/${accountId}/transactions`, { params: { page, pageSize } }),

  // 建立銀行交易
  createBankTransaction: (accountId: string, data: any) =>
    api.post(`/bank-accounts/${accountId}/transactions`, data),
  // 刪除銀行交易
  deleteBankTransaction: (accountId: string, transactionId: string) =>
    api.delete(`/bank-accounts/${accountId}/transactions/${transactionId}`),

  // 獲取單一帳戶摘要統計
  getBankAccountDetailSummary: (accountId: string) =>
    api.get(`/bank-accounts/${accountId}/summary`)
};

// 批量刪除銀行交易記錄
export const bulkDeleteBankTransactions = (accountId: string, transactionIds: string[]) =>
  api.delete(`/bank-accounts/${accountId}/transactions`, { data: { transactionIds } });

// === 銀行對帳管理 APIs ===

export const bankReconciliationService = {
  // 檢測檔案欄位映射
  detectFields: (file: FormData) =>
    api.post('/bank-reconciliation/detect-fields', file, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // 匯入對帳單
  importStatement: (bankAccountId: string, formData: FormData, customMapping?: any) => {
    if (customMapping) {
      formData.append('mapping', JSON.stringify(customMapping));
    }
    return api.post(`/bank-reconciliation/import/${bankAccountId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // 獲取匯入記錄列表
  getImportRecords: (params: { bankAccountId?: string; page?: number; pageSize?: number }) =>
    api.get('/bank-reconciliation/imports', { params }),

  // 獲取匯入記錄詳情
  getImportRecordById: (importId: string) =>
    api.get(`/bank-reconciliation/imports/${importId}`),

  // 獲取匯入的交易記錄
  getImportedTransactions: (importId: string, page: number = 1, pageSize: number = 20) =>
    api.get(`/bank-reconciliation/imports/${importId}/transactions`, { params: { page, pageSize } }),

  // 刪除匯入記錄
  deleteImportRecord: (importId: string) =>
    api.delete(`/bank-reconciliation/imports/${importId}`),

  // 獲取支援的銀行格式範本
  getBankTemplates: () =>
    api.get('/bank-reconciliation/templates'),

  // 執行智能交易比對
  performAutoMatch: (bankAccountId: string, rules?: any, autoApplyThreshold?: number) =>
    api.post(`/transaction-matching/auto-match/${bankAccountId}`, { rules, autoApplyThreshold }),

  // 手動應用匹配
  applyManualMatch: (bankTransactionId: string, statementTransactionId: string) =>
    api.post('/transaction-matching/manual-match', { bankTransactionId, statementTransactionId }),

  // 取消匹配
  unmatch: (bankTransactionId: string) =>
    api.post(`/transaction-matching/unmatch/${bankTransactionId}`),

  // 獲取匹配統計
  getMatchingStatistics: (bankAccountId: string) =>
    api.get(`/transaction-matching/statistics/${bankAccountId}`),

  // 獲取預設比對規則
  getDefaultRules: () =>
    api.get('/transaction-matching/rules/default'),

  // 驗證比對規則
  validateRules: (rules: any) =>
    api.post('/transaction-matching/rules/validate', { rules }),

  // 獲取比對差異報告
  getDifferencesReport: (bankAccountId: string, page: number = 1, pageSize: number = 20) =>
    api.get(`/transaction-matching/differences/${bankAccountId}`, { params: { page, pageSize } })
};

// 會計年度管理 API
export const fiscalYearService = {
  // 獲取所有會計年度
  getFiscalYears: (page: number = 1, pageSize: number = 50) =>
    api.get('/fiscal-years', { params: { page, pageSize } }),

  // 獲取活躍會計年度
  getActiveFiscalYear: () =>
    api.get('/fiscal-years/active'),

  // 根據ID獲取會計年度
  getFiscalYearById: (id: string) =>
    api.get(`/fiscal-years/${id}`),

  // 創建會計年度
  createFiscalYear: (data: {
    name: string;
    startDate: string;
    endDate: string;
    description?: string;
    isActive?: boolean;
  }) =>
    api.post('/fiscal-years', data),

  // 自動生成會計期間
  generateFiscalYearPeriods: (fiscalYearId: string) =>
    api.post(`/fiscal-years/${fiscalYearId}/generate-periods`)
};

// 會計期間管理 API
export const periodService = {
  // 創建會計期間
  createPeriod: (data: {
    fiscalYearId: string;
    name: string;
    startDate: string;
    endDate: string;
    description?: string;
  }) =>
    api.post('/periods', data)
};

// === 資料匯入/匯出 APIs ===

// 匯出為 Excel
export const exportToExcel = (sheets: string[]) => {
  return api.post('/data-io/export/excel', { sheets, language: 'zh' }, {
    responseType: 'blob',
  });
};

// 從 Excel 匯入
export const importFromExcel = (file: File, onUploadProgress?: (progressEvent: any) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('language', 'zh');
  return api.post('/data-io/import/excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

export default api;